<template>
  <div>
    <b-modal
      id="create-meeting"
      ref="modal"
      centered
      hide-header
      hide-footer
      @show="reset"
    >
      <div class="mb-4 text-center">
        <h2 class="mt-2 mb-4">
          Create {{ isScheduled ? "Meeting" : "Quick Link" }}
        </h2>
        <b-radio-group
          v-model="isScheduled"
          :options="[
            { text: 'Quick Link', value: false },
            { text: 'Schedule', value: true }
          ]"
          class="w-75"
          button-variant="outline-primary"
          buttons
        />

        <div class="text-muted mt-2">
          {{
            isScheduled
              ? "Schedule a meeting in Nanome and invite through your calendar"
              : "Quickly generate a URL link and share"
          }}
        </div>
      </div>

      <NanInput
        v-model="meeting.room"
        :validation="$v.meeting.room"
        label="Room Name"
        class="mb-2"
      />

      <NanInput
        v-model="hasPassword"
        label="Require password?"
        type="checkbox"
        class="mb-2"
      />

      <NanInput
        v-if="hasPassword"
        v-model="meeting.password"
        :validation="$v.meeting.password"
        label="Password"
        autofocus
      />

      <hr />

      <template v-if="isScheduled">
        <h4>Event Information</h4>

        <NanInput
          v-model="meeting.title"
          :validation="$v.meeting.title"
          label="Event Title"
          class="mb-2"
        />

        <NanInput
          v-model="meeting.description"
          :validation="$v.meeting.description"
          label="Description"
          type="textarea"
          rows="3"
          class="mb-2"
        />

        <b-form-group
          :state="!$v.meeting.start.$error && null"
          label="Date &amp; Time"
          invalid-feedback="These fields are required."
        >
          <b-form-datepicker v-model="event.date" class="mb-2" />
          <div class="d-flex align-items-center">
            <NanInput
              v-model="event.start"
              :options="timeOptions"
              prepend-icon="icon-clock"
              type="select"
              class="flex-fill"
            />

            <div class="mx-2">to</div>

            <NanInput
              v-model="event.end"
              :options="timeOptions"
              prepend-icon="icon-clock"
              type="select"
              class="flex-fill"
            />
          </div>
        </b-form-group>

        <hr />
      </template>

      <NanInput
        v-if="!isScheduled"
        v-model="linkExpiration"
        :options="[
          { label: '10 days', value: 10 },
          { label: '30 days', value: 30 },
          { label: '60 days', value: 60 },
          { label: '90 days', value: 90 }
        ]"
        label="Remove from my account after"
        type="select"
      />

      <NanInput
        v-if="isScheduled"
        v-model="addToNanome"
        label="Add to Nanome scheduled meetings"
        type="checkbox"
      />

      <b-form-row class="mt-4">
        <b-col>
          <b-btn variant="outline-secondary" block @click="$refs.modal.hide()">
            Cancel
          </b-btn>
        </b-col>
        <b-col>
          <b-btn
            :class="{ disabled: $v.$invalid }"
            variant="primary"
            block
            @click="save"
          >
            Save and Continue
          </b-btn>
        </b-col>
      </b-form-row>
    </b-modal>

    <b-modal
      ref="success"
      body-class="bg-gray-100 rounded"
      centered
      hide-header
      hide-footer
    >
      <h3 class="mb-3 text-center">
        {{ isScheduled ? "Meeting" : "Link" }} Created
      </h3>

      <div class="mb-3 p-4 bg-white rounded">
        <MeetingDetails :meeting="meeting" />
      </div>

      <div class="text-center">
        <b-btn
          class="px-5"
          variant="outline-primary"
          @click="$refs.success.hide()"
        >
          Done
        </b-btn>
      </div>
    </b-modal>
  </div>
</template>

<script>
import moment from "moment";
import { validationMixin } from "vuelidate";
import { required, requiredIf } from "vuelidate/lib/validators";

import API from "@/api";
import MeetingDetails from "@/components/meetings/MeetingDetails";

const meetingDefaults = {
  id: "",
  room: "",
  password: "",
  title: "",
  description: "",
  start: "",
  end: ""
};

const eventDefaults = {
  date: "",
  start: "",
  end: ""
};

export default {
  mixins: [validationMixin],

  components: { MeetingDetails },

  data: () => ({
    addToNanome: true,
    isScheduled: false,
    hasPassword: false,
    linkExpiration: 30,
    meeting: { ...meetingDefaults },
    event: { ...eventDefaults }
  }),

  computed: {
    timeOptions() {
      const options = [];
      const time = moment().startOf("day");
      for (let i = 0; i < 96; i++) {
        options.push(time.format("LT"));
        time.add(15, "minutes");
      }
      return options;
    }
  },

  watch: {
    event: {
      handler(e) {
        if (!e.date || !e.start || !e.end) return;

        const format = "YYYY-MM-DD LT";
        const start = moment(`${e.date} ${e.start}`, format);
        const end = moment(`${e.date} ${e.end}`, format);

        if (end < start) end.add(1, "day");
        this.meeting.start = start.utc().format();
        this.meeting.end = end.format();
      },
      deep: true
    },
    "event.start"(newStart, oldStart) {
      newStart = moment(newStart, "LT");
      let minutes = 60;
      if (this.event.end && oldStart) {
        oldStart = moment(oldStart, "LT");
        const end = moment(this.event.end, "LT");
        minutes = end.diff(oldStart, "minutes");
      }
      this.event.end = newStart.add(minutes, "minutes").format("LT");
    }
  },

  created() {
    const createMeeting = v => {
      this.isScheduled = v;
      this.$refs.modal.show();
    };
    this.$root.$on("create-meeting", createMeeting);
    this.$once("hook:beforeDestroy", () => {
      this.$root.$off("create-meeting", createMeeting);
    });
  },

  methods: {
    reset() {
      this.$v.$reset();
      this.meeting = { ...meetingDefaults };
      this.event = { ...eventDefaults };
      this.meeting.id = Array(9)
        .fill(0)
        .map(() => Math.floor(10 * Math.random()))
        .join("")
        .replace(/(.{3})(?!$)/g, "$1-");
    },

    async save() {
      if (this.$v.$invalid) {
        this.$v.$touch();
        return;
      }

      if (this.addToNanome) {
        const { id, room, password } = this.meeting;
        const meeting = { id, room, password };
        if (this.isScheduled) {
          Object.assign(meeting, this.meeting);
        } else {
          meeting.exp = moment()
            .add(this.linkExpiration, "days")
            .toISOString();
        }
        if (!this.hasPassword) delete meeting.password;

        const res = await API.addMeeting(meeting);
        if (!res.success) return;
      }

      this.$refs.modal.hide();
      this.$refs.success.show();

      if (this.$route.name !== "Meetings") {
        this.$router.push({ name: "Meetings" });
      } else {
        this.$root.$emit("refresh-meetings");
      }
    }
  },

  validations() {
    return {
      meeting: {
        room: { required },
        password: { required: requiredIf(() => this.hasPassword) },
        title: { required: requiredIf(() => this.isScheduled) },
        description: { required: requiredIf(() => this.isScheduled) },
        start: { required: requiredIf(() => this.isScheduled) }
      }
    };
  }
};
</script>
